import { z } from 'zod';
/**
 * Branded primitives.
 *
 * A brand makes two structurally identical strings incompatible at compile time,
 * so a `TickerSymbol` can never be passed where an `IsoDate` is expected.
 *
 * Each primitive comes in two flavours, and the split is load-bearing:
 *
 *  - the plain schema *describes* a value and is safe to use in responses;
 *  - the `…Input` schema additionally *normalises* (trim, upper case) and is only
 *    used for request payloads.
 *
 * The reason is that Fastify serialises responses through the same Zod schema it
 * validates with, running it in encode direction. A `.transform()` is
 * unidirectional and cannot be encoded, so a response schema containing one fails
 * at runtime — as a 500, not as a type error. Keeping transforms out of the domain
 * schemas is what prevents that whole class of bug.
 *
 * Checks that are *not* transforms — `.brand()`, `.refine()`, `.default()` and
 * `z.coerce` — are encode-safe and may appear anywhere.
 */
const SYMBOL_PATTERN = /^[A-Z0-9.\-:/]+$/;
/** Ticker symbol in its normalised form: upper case, no surrounding whitespace. */
export const tickerSymbolSchema = z
    .string()
    .min(1, 'Symbol darf nicht leer sein')
    .max(24, 'Symbol ist zu lang')
    .regex(SYMBOL_PATTERN, 'Symbol enthält unzulässige Zeichen')
    .brand();
/** Accepts user input in any casing and normalises it. Request payloads only. */
export const tickerSymbolInputSchema = z
    .string()
    .trim()
    .min(1, 'Symbol darf nicht leer sein')
    .max(24, 'Symbol ist zu lang')
    .transform((value) => value.toUpperCase())
    .pipe(tickerSymbolSchema);
/** Calendar day in `YYYY-MM-DD`. Trading days are dates, never timestamps. */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum muss dem Format YYYY-MM-DD entsprechen')
    .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Ungültiges Datum')
    .brand();
/**
 * ISIN — International Securities Identification Number.
 *
 * Zwölf Zeichen: zwei Buchstaben Länderkennung, neun alphanumerische Stellen, eine
 * Prüfziffer. Anders als ein Ticker ist sie **weltweit eindeutig**: `SAP` bezeichnet
 * je nach Börse drei verschiedene Unternehmen, `DE0007164600` immer dasselbe Papier.
 * Deshalb ist sie der verlässlichere Schlüssel, wenn Kurse aus mehreren Quellen
 * zusammenkommen.
 */
export const isinSchema = z
    .string()
    .regex(/^[A-Z]{2}[A-Z0-9]{9}\d$/, 'ISIN muss zwölfstellig sein, z. B. US0378331005')
    .brand();
/** Nimmt Eingaben in beliebiger Schreibweise und mit Leerzeichen entgegen. */
export const isinInputSchema = z
    .string()
    .trim()
    .transform((value) => value.replace(/\s+/g, '').toUpperCase())
    .pipe(isinSchema);
export const asIsin = (value) => value;
/**
 * Prüft die Prüfziffer nach dem Luhn-Verfahren.
 *
 * Fängt Zahlendreher ab, die das Muster allein durchlässt — bei einem Schlüssel,
 * der über Jahre Kurse zuordnet, ist ein stiller Tippfehler teuer.
 */
export const isValidIsinChecksum = (isin) => {
    if (!/^[A-Z]{2}[A-Z0-9]{9}\d$/.test(isin))
        return false;
    /* Buchstaben werden zu Zahlen: A = 10 … Z = 35. */
    const digits = [...isin]
        .map((character) => /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character)
        .join('');
    let sum = 0;
    let double = true;
    for (let i = digits.length - 2; i >= 0; i -= 1) {
        const value = Number(digits[i]);
        const scaled = double ? value * 2 : value;
        sum += scaled > 9 ? scaled - 9 : scaled;
        double = !double;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === Number(digits.at(-1));
};
/** Positive database identifier. */
export const idSchema = z.coerce.number().int().positive().brand();
/**
 * ISO-4217 currency code in its normalised form.
 *
 * Deliberately not branded: unlike a ticker or a date, a currency only ever
 * appears as a `currency` field and is never at risk of being confused with
 * another string, so a brand would add casts without preventing a real mistake.
 */
export const currencySchema = z.string().regex(/^[A-Z]{3}$/, 'Währung muss ein ISO-4217-Code sein');
/** Accepts a currency in any casing and normalises it. Request payloads only. */
export const currencyInputSchema = z
    .string()
    .trim()
    .length(3, 'Währung muss dreistellig sein')
    .transform((value) => value.toUpperCase())
    .pipe(currencySchema);
/** Unsafe casts for trusted sources (database rows, literals in tests). */
export const asTickerSymbol = (value) => value;
export const asIsoDate = (value) => value;
export const asEntityId = (value) => value;
//# sourceMappingURL=branded.js.map